import React from 'react'
import {
  theme,
  IconPrimaryApp,
  IconChildApp
} from '@contrast-security-inc/design-system-foundations'
import { css, cx } from '@emotion/css'
import { AgentFeature } from '../agent-features'
import { VulnerabilityApplicationTooltipContent } from '../application-tooltip'
import { OverflowDetector } from '../../legacy-design-system/overflow-detector'
import { getApplicationDrilldownRoute } from '../route'
import { WithTooltip } from '../../design-system'
import { VulnerabilityApplication } from './shared'
import { Role } from '@contrast-security-inc/base-types'

const appNameTargetContainerCss = css`
  flex-shrink: 0;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  line-height: 1.2em;
  white-space: nowrap;
`

const noWrapSpaceCss = css`
  white-space: nowrap;
`

const appNameTextCss = cx(
  noWrapSpaceCss,
  css`
    color: ${theme.color.neutral100};
    cursor: pointer;

    &:hover {
      color: ${theme.color.neutral100};
      text-decoration: underline;
    }
  `
)

interface Props {
  readonly organizationID: string
  readonly organizationRole: Role
  readonly hasLicenses: boolean
  readonly application: VulnerabilityApplication
  readonly features: AgentFeature[]
  readonly clickable?: boolean
}

interface State {
  readonly overflowing: boolean
}

export class ApplicationTooltip extends React.Component<Props, State> {
  public constructor(props) {
    super(props)
    this.state = {
      overflowing: false
    }
  }
  public render() {
    const {
      organizationID,
      organizationRole,
      hasLicenses,
      application,
      features,
      clickable = true
    } = this.props

    const renderAppTitle = () => {
      const ApplicationIcon = application.parentApplicationId
        ? IconChildApp
        : application.isParentApp
        ? IconPrimaryApp
        : undefined

      return (
        <>
          {ApplicationIcon && (
            <span
              className={css`
                vertical-align: middle;
                padding-right: 4px;
              `}
            >
              <ApplicationIcon size="sm" />
            </span>
          )}
          {application.name}
        </>
      )
    }

    return (
      <OverflowDetector>
        {({ doesOverflowParent, registerElement }) => {
          return (
            <WithTooltip
              renderTooltipContent={() => {
                return (
                  <VulnerabilityApplicationTooltipContent
                    organizationID={organizationID}
                    organizationRole={organizationRole}
                    hasLicenses={hasLicenses}
                    features={features}
                    showName={this.state.overflowing}
                    id={application.id}
                    name={application.name}
                    url={application.url}
                    importance={application.importance}
                    licensed={application.licensed}
                    language={application.language}
                  />
                )
              }}
              children={({ ref, onMouseLeave, onMouseEnter }) => (
                <span
                  ref={ref}
                  className={appNameTargetContainerCss}
                  onMouseEnter={() => {
                    this.updateOverflowing(doesOverflowParent())
                    if (onMouseEnter) {
                      onMouseEnter()
                    }
                  }}
                  onMouseLeave={onMouseLeave}
                >
                  {clickable ? (
                    <a
                      ref={registerElement}
                      className={appNameTextCss}
                      href={getApplicationDrilldownRoute(
                        organizationID,
                        getLinkApplicationId(application)
                      )}
                      data-e2e="app-name"
                    >
                      {renderAppTitle()}
                    </a>
                  ) : (
                    <>{renderAppTitle()}</>
                  )}
                </span>
              )}
            />
          )
        }}
      </OverflowDetector>
    )
  }

  private updateOverflowing = (overflowing: boolean) =>
    this.setState({ overflowing })
}

// If the application has a parent, then we want to link
// to the parent application rather than the app itself.
function getLinkApplicationId({
  id,
  parentApplicationId
}: VulnerabilityApplication): string {
  return parentApplicationId || id
}
